import { useEffect, useRef, useState } from "react";

// ─────────────────────────── Medalhas conquistadas ───────────────────────────

type Badge = { code: string; name: string; icon: string | null; description: string | null };

/** Exibe, por alguns segundos, as medalhas recém-conquistadas (anunciadas pela camada de API). */
export function BadgeToaster() {
  const [items, setItems] = useState<Array<Badge & { key: number }>>([]);
  useEffect(() => {
    const onBadges = (e: Event) => {
      const list = (e as CustomEvent<Badge[]>).detail.map((b, i) => ({ ...b, key: Date.now() + i }));
      setItems((cur) => [...cur, ...list]);
      for (const b of list) setTimeout(() => setItems((cur) => cur.filter((x) => x.key !== b.key)), 6000);
    };
    window.addEventListener("b2c:badges", onBadges);
    return () => window.removeEventListener("b2c:badges", onBadges);
  }, []);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((b) => (
        <div key={b.key} className="toast card">
          <span className="toast-icon" aria-hidden>
            {b.icon}
          </span>
          <div>
            <strong>Nova medalha: {b.name}</strong>
            <div className="segment-meta">{b.description}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────── Tamanho da fonte ───────────────────────────

const SIZES = [90, 100, 112, 125, 140];
const readSize = () => {
  try {
    const v = Number(localStorage.getItem("b2c-font"));
    return SIZES.includes(v) ? v : 100;
  } catch {
    return 100;
  }
};
export const applyFontSize = (v = readSize()) => {
  document.documentElement.style.fontSize = `${v}%`;
};

/** Ajuste do tamanho do texto (acessibilidade), lembrado no navegador. */
export function FontSizeControl() {
  const [size, setSize] = useState(readSize);
  const change = (dir: 1 | -1) => {
    const next = SIZES[Math.min(SIZES.length - 1, Math.max(0, SIZES.indexOf(size) + dir))]!;
    setSize(next);
    applyFontSize(next);
    try {
      localStorage.setItem("b2c-font", String(next));
    } catch {
      /* armazenamento indisponível */
    }
  };
  return (
    <div className="font-ctl" role="group" aria-label="Tamanho do texto">
      <button
        type="button"
        onClick={() => change(-1)}
        disabled={size === SIZES[0]}
        aria-label="Diminuir texto"
      >
        A−
      </button>
      <button
        type="button"
        onClick={() => change(1)}
        disabled={size === SIZES[SIZES.length - 1]}
        aria-label="Aumentar texto"
      >
        A+
      </button>
    </div>
  );
}

// ─────────────────────────── Narração com voz neural ───────────────────────────

const RATES = [0.8, 1, 1.25, 1.5, 1.75];

/** Player da narração gerada na publicação, com velocidade e retomada do ponto em que o aluno parou. */
export function NarrationPlayer({ src, storageKey }: { src: string; storageKey: string }) {
  const ref = useRef<HTMLAudioElement>(null);
  const [rate, setRate] = useState(1);
  const key = `b2c-narracao:${storageKey}`;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    try {
      const saved = Number(localStorage.getItem(key) || 0);
      if (saved > 0) el.currentTime = saved;
    } catch {
      /* armazenamento indisponível: começa do início */
    }
    const save = () => {
      try {
        localStorage.setItem(key, String(Math.floor(el.currentTime)));
      } catch {
        /* sem armazenamento local */
      }
    };
    el.addEventListener("pause", save);
    el.addEventListener("timeupdate", save);
    return () => {
      el.removeEventListener("pause", save);
      el.removeEventListener("timeupdate", save);
    };
  }, [key]);
  return (
    <div className="narration" role="group" aria-label="Narração da aula">
      <audio ref={ref} src={src} controls preload="none" />
      <select
        aria-label="Velocidade da narração"
        value={rate}
        onChange={(e) => {
          const v = Number(e.target.value);
          setRate(v);
          if (ref.current) ref.current.playbackRate = v;
        }}
      >
        {RATES.map((r) => (
          <option key={r} value={r}>
            {String(r).replace(".", ",")}×
          </option>
        ))}
      </select>
      <span className="segment-meta">Narração com voz sintética</span>
    </div>
  );
}

// ─────────────────────────── Leitura em voz alta ───────────────────────────

/** Narração da aula com a voz do próprio navegador (sem custo), em português do Brasil. */
export function ReadAloud({ getText }: { getText: () => string }) {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [state, setState] = useState<"idle" | "playing" | "paused">("idle");
  const [rate, setRate] = useState(1);
  const queue = useRef<string[]>([]);

  useEffect(() => () => window.speechSynthesis?.cancel(), []);
  if (!supported) return null;

  const speakNext = () => {
    const next = queue.current.shift();
    if (!next) return setState("idle");
    const u = new SpeechSynthesisUtterance(next);
    u.lang = "pt-BR";
    u.rate = rate;
    const voice = speechSynthesis.getVoices().find((v) => v.lang.toLowerCase().startsWith("pt"));
    if (voice) u.voice = voice;
    u.onend = speakNext;
    u.onerror = () => setState("idle");
    speechSynthesis.speak(u);
  };
  const start = () => {
    speechSynthesis.cancel();
    // Frases curtas evitam o corte de textos longos em alguns navegadores.
    queue.current = getText()
      .replace(/[#*_>`]/g, "")
      .split(/(?<=[.!?:])\s+|\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setState("playing");
    speakNext();
  };

  return (
    <div className="read-aloud" role="group" aria-label="Leitura em voz alta">
      {state === "idle" ? (
        <button type="button" className="btn ghost small" onClick={start}>
          🔊 Ouvir aula
        </button>
      ) : (
        <>
          <button
            type="button"
            className="btn ghost small"
            onClick={() => {
              if (state === "playing") {
                speechSynthesis.pause();
                setState("paused");
              } else {
                speechSynthesis.resume();
                setState("playing");
              }
            }}
          >
            {state === "playing" ? "⏸ Pausar" : "▶ Continuar"}
          </button>
          <button
            type="button"
            className="btn ghost small"
            onClick={() => {
              queue.current = [];
              speechSynthesis.cancel();
              setState("idle");
            }}
          >
            ⏹ Parar
          </button>
        </>
      )}
      <select
        aria-label="Velocidade da leitura"
        value={rate}
        onChange={(e) => setRate(Number(e.target.value))}
        className="rate"
      >
        {[0.8, 1, 1.2, 1.5].map((r) => (
          <option key={r} value={r}>
            {String(r).replace(".", ",")}×
          </option>
        ))}
      </select>
    </div>
  );
}

// ─────────────────────────── Sem conexão ───────────────────────────

/** Aviso discreto quando o aparelho fica sem internet (as aulas já abertas continuam disponíveis). */
export function OfflineBanner() {
  const [offline, setOffline] = useState(() => typeof navigator !== "undefined" && !navigator.onLine);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  if (!offline) return null;
  return (
    <div className="offline-banner" role="status">
      Você está sem internet. As aulas que já foram abertas continuam disponíveis para leitura.
    </div>
  );
}
